import React, { useRef, useEffect } from 'react';
import videojs from 'video.js';
import Hls from 'hls.js';
import 'video.js/dist/video-js.css';
import './VideoPlayer.scss';
import { v4 as uuidv4 } from 'uuid';

interface VideoPlayerProps {
    videoSource: string;
    isHls?: boolean;
    isRecordingEnabled: boolean;
    completeRecording?: (startTime: number, endTime: number) => void;
    startTimeSeconds?: number;
    endTimeSeconds?: number;
    subtitlesSource?: string;
    hideContextMenu?: boolean;
}

export const VideoPlayer = (props: VideoPlayerProps) => {
    const guid = 'middleware-' + uuidv4();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<Hls | null>(null);

    useEffect(() => {
        if (videoRef.current) {
            const player: any = videojs(videoRef.current, {
                controls: true,
                fluid: true,
                middlewareId:
                    (props.startTimeSeconds || props.startTimeSeconds === 0) &&
                    (props.endTimeSeconds || props.endTimeSeconds === 0)
                        ? guid
                        : undefined,
            });

            if (props.isHls) {
                if (Hls.isSupported()) {
                    const hls = new Hls({ xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; } });
                    hls.loadSource(props.videoSource);
                    hls.attachMedia(videoRef.current!);
                    hlsRef.current = hls;
                } else if (videoRef.current!.canPlayType('application/vnd.apple.mpegurl')) {
                    player.src({ src: props.videoSource, type: 'application/vnd.apple.mpegurl' });
                }
            } else {
                player.src({ src: props.videoSource, type: 'video/mp4' });
            }

            if (props.subtitlesSource) {
                player.addRemoteTextTrack({
                    src: props.subtitlesSource,
                    kind: 'subtitles',
                    srclang: 'en',
                    label: 'English',
                    default: false,
                });
            }

            if (props.hideContextMenu) {
                const preventContextMenu = (e: Event) => {
                    e.preventDefault();
                    return false;
                };
                
                const disableContextMenuOnElement = (element: HTMLElement) => {
                    if (element) {
                        element.addEventListener('contextmenu', preventContextMenu, true);
                        element.setAttribute('oncontextmenu', 'return false;');
                    }
                };
                
                const playerElement = player.el();
                disableContextMenuOnElement(playerElement);
                playerElement.style.userSelect = 'none';
                
                const videoElement = playerElement.querySelector('video');
                if (videoElement) {
                    disableContextMenuOnElement(videoElement);
                }
                
                player.ready(() => {
                    const controlBar = playerElement.querySelector('.vjs-control-bar');
                    if (controlBar) {
                        disableContextMenuOnElement(controlBar);
                        const allControls = controlBar.querySelectorAll('*');
                        allControls.forEach((control: Element) => {
                            disableContextMenuOnElement(control as HTMLElement);
                        });
                    }
                });
            }

            if (props.isRecordingEnabled) {
                var recordButton = player.controlBar.addChild('button', {}, 1);
                var recordButtonDom = recordButton.el();
                recordButtonDom.title = 'Record';
                recordButton.addClass('vjs-icon-circle-inner-circle');
                let recordingStartTime: any = undefined;
                recordButtonDom.onclick = () => {
                    if (!recordingStartTime && recordingStartTime !== 0) {
                        recordingStartTime = player.currentTime();
                        recordButton.addClass('color-red');
                    } else {
                        recordButton.removeClass('color-red');
                        const currentTime = player.currentTime();
                        if (Math.abs(recordingStartTime - currentTime) < 1) {
                            recordingStartTime = undefined;
                            return;
                        }
                        props.completeRecording(
                            recordingStartTime > currentTime
                                ? currentTime
                                : recordingStartTime,
                            currentTime > recordingStartTime
                                ? currentTime
                                : recordingStartTime
                        );
                        player.pause();
                        recordingStartTime = undefined;
                    }
                };
            }

            if (
                (props.startTimeSeconds || props.startTimeSeconds === 0) &&
                (props.endTimeSeconds || props.endTimeSeconds === 0)
            ) {
                player.on('timeupdate', function () {
                    const currentTime = player.currentTime();
                    if (
                        currentTime >
                        props.endTimeSeconds - props.startTimeSeconds
                    ) {
                        player.currentTime(0);
                        player.pause();
                    }
                });

                let clipMiddleware = function (player: any) {
                    return {
                        setSource(srcObj: any, next: any) {
                            if (player.options().middlewareId === guid) {
                                next(null, srcObj);
                            } else {
                                next(new Error("don't use middleware"));
                            }
                        },
                        currentTime: function (ct: any) {
                            return ct - props.startTimeSeconds;
                        },
                        duration: function () {
                            return (
                                props.endTimeSeconds - props.startTimeSeconds
                            );
                        },
                        setCurrentTime: function (time: any) {
                            return time + props.startTimeSeconds;
                        },
                    };
                };

                videojs.use('*', clipMiddleware);
                player.currentTime(0);
            }

            return () => {
                hlsRef.current?.destroy();
                hlsRef.current = null;
                if (player) {
                    player.dispose();
                }
            };
        }
    }, [props.videoSource]);

    return props.videoSource ? (
        <div className={`video-player ${props.hideContextMenu ? 'hide-context-menu' : ''}`}>
            <video
                ref={videoRef}
                className="video-js"
                data-setup='{"playbackRates": [0.25, 0.5, 1, 1.5, 2]}'
            />
        </div>
    ) : (
        <></>
    );
};

export default VideoPlayer;
